<template>
    <div class="fixed block z-50 mt-8 mr-8" :style="styles">
        <transition-group
            enter-from-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
            enter-active-class="ease-out duration-300"
            enter-to-class="opacity-100 translate-y-0 sm:scale-100"
            leave-from-class="opacity-100 translate-y-0 sm:scale-100"
            leave-active-class="ease-in duration-200"
            leave-to-class="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95">

            <div v-for="item in active"
                 class="block overflow-hidden mb-6 py-3 px-4 bg-white border border-gray-300 rounded transition ease-in duration-300 shadow"
                 :key="item.id"
                 :data-id="item.id">
                <div :class="[classes, item.type]">
                    <slot name="body"
                          :item="item"
                          :close="() => destroy(item)">
                        <div class="flex w-full" @click.left="destroyIfNecessary(item)">
                            <div v-if="hasValidType(item)" class="relative mr-4">
                                <svg class="w-8 h-8"
                                     :class="{
                                    'text-green-500':item.type === 'success',
                                    'text-blue-500':item.type === 'info',
                                    'text-yellow-500':item.type === 'warning',
                                    'text-red-500':item.type === 'error'
                                 }"
                                     fill="none"
                                     stroke-linecap="round"
                                     stroke-linejoin="round"
                                     stroke-width="2"
                                     viewBox="0 0 24 24"
                                     stroke="currentColor">
                                    <path v-if="item.type === 'success'"
                                          d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    <path v-if="item.type === 'info'"
                                          d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    <path v-if="item.type === 'warning'"
                                          d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                    <path v-if="item.type === 'error'"
                                          d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
                                </svg>
                            </div>
                            <div class="text-gray-900">
                                <div v-if="item.title" class="text-lg mb-1 font-bold" v-html="item.title"></div>
                                <div class="text-gray-800" v-html="item.text"></div>
                            </div>
                        </div>
                    </slot>
                </div>
            </div>
        </transition-group>
    </div>
</template>
<script>

import {uniqueId} from "lodash"

const TYPES = [
    'success',
    'info',
    'warning',
    'error'
];

export default {

    name: 'Notifications',

    props: {
        width: {
            type: [Number, String],
            default: 300
        },
        reverse: {
            type: Boolean,
            default: false
        },
        position: {
            type: [String, Array],
            default: () => ['top', 'right']
        },
        classes: {
            type: String,
            default: ''
        },
        speed: {
            type: Number,
            default: 300
        },
        duration: {
            type: Number,
            default: 4000
        },
        delay: {
            type: Number,
            default: 0
        },
        max: {
            type: Number,
            default: Infinity
        },
        ignoreDuplicates: {
            type: Boolean,
            default: false
        },
        closeOnClick: {
            type: Boolean,
            default: true
        }
    },

    data() {
        return {
            list: [],
        }
    },

    created() {
        document.addEventListener('notifications:push', this.addItem);
        document.addEventListener('notifications:close', this.closeItem);
    },

    methods: {
        hasValidType(item) {
            return TYPES.includes(item.type);
        },
        listToDirection(value) {
            if (typeof value === 'string') {
                value = value.split(/\s+/gi).filter(v => v);
            }

            const directions = {
                x: ['left', 'center', 'right'],
                y: ['top', 'bottom']
            };

            let x = null,
                y = null;

            value.forEach(v => {
                if (directions.y.indexOf(v) !== -1) {
                    y = v
                }
                if (directions.x.indexOf(v) !== -1) {
                    x = v
                }
            })

            return {x, y}
        },
        destroyIfNecessary(item) {
            if (this.closeOnClick) {
                this.destroy(item);
            }
        },
        addItem(e) {

            const detail = e instanceof CustomEvent ? e.detail : e;

            if (detail.clean || detail.clear) {
                this.destroyAll();
                return;
            }

            const duration = typeof detail.duration === 'number'
                ? detail.duration
                : this.duration

            const speed = typeof detail.speed === 'number'
                ? detail.speed
                : this.speed

            const ignoreDuplicates = typeof detail.ignoreDuplicates === 'boolean'
                ? detail.ignoreDuplicates
                : this.ignoreDuplicates

            let {title, text, type, data, id} = detail

            const item = {
                id: id || uniqueId('notification'),
                title,
                text,
                type,
                active: true,
                speed,
                length: duration + 2 * speed,
                data
            }

            if (duration > 0) {
                item.timer = setTimeout(() => this.destroy(item), item.length)
            }

            let direction = this.reverse ? !this.botToTop : this.botToTop;

            let indexToDestroy = -1;

            const isDuplicate = this.active.some(item => {
                return item.title === detail.title && item.text === detail.text
            });

            if (ignoreDuplicates && isDuplicate) {
                return;
            }

            if (direction) {

                this.list.push(item);

                if (this.active.length > this.max) {
                    indexToDestroy = 0
                }
            } else {
                this.list.unshift(item)

                if (this.active.length > this.max) {
                    indexToDestroy = this.active.length - 1
                }
            }

            if (indexToDestroy !== -1) {
                this.destroy(this.active[indexToDestroy])
            }
        },

        closeItem(id) {
            this.destroyById(id)
        },
        notifyClass(item) {
            return [
                'vue-notification-template',
                this.classes,
                item.type
            ]
        },
        notifyWrapperStyle(item) {
            return this.isVA
                ? null
                : {transition: `all ${item.speed}ms`}
        },
        destroy(item) {
            clearTimeout(item.timer);
            item.active = false;
            this.clean();
        },
        destroyById(id) {
            const item = this.list.find(v => v.id === id)

            if (item) {
                this.destroy(item)
            }
        },
        destroyAll() {
            this.active.forEach(this.destroy)
        },
        clean() {
            this.list = this.list.filter(v => v.active)
        }
    },

    computed: {
        actualWidth() {
            switch (typeof this.width) {
                case 'number':
                    return {type: 'px', value: this.width}
                case 'string':
                    return this.getType(this.width)
                default:
                    return {type: '', value: this.width}
            }
        },
        getType: (value) => {
            if (value === 'auto') {
                return {
                    type: value,
                    value: 0
                }
            }

            for (var i = 0; i < types.length; i++) {
                let type = types[i]
                if (type.regexp.test(value)) {
                    return {
                        type: type.name,
                        value: parseFloat(value)
                    }
                }
            }

            return {
                type: '',
                value: value
            }
        },
        styles() {
            const {x, y} = this.listToDirection(this.position);
            const width = this.actualWidth.value;
            const suffix = this.actualWidth.type;

            let styles = {
                width: width + suffix,
                [y]: '0px'
            }

            if (x === 'center') {
                styles['left'] = `calc(50% - ${width / 2}${suffix})`
            } else {
                styles[x] = '0px'
            }

            return styles
        },
        active() {
            return this.list.filter(v => v.active);
        },
        botToTop() {
            return this.styles.hasOwnProperty('bottom')
        },
    },

    beforeUnmount() {
        document.removeEventListener('notifications:push', this.addItem);
        document.removeEventListener('notifications:close', this.closeItem);
    },
};

</script>
